import React, { useState, useEffect, useCallback } from 'react';
import { getFormasPagoPaginadas, deleteFormaPago } from '../services/formaPagoService';
import { Link, useNavigate } from 'react-router-dom';
import { PlusCircle, Edit, Trash2, ArrowLeft, RefreshCw, AlertCircle, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, CreditCard } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export default function FormasPagoList() {
    const [formasPago, setFormasPago] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination & Search States
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const navigate = useNavigate();
    const { token } = useAuth();

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const cargarFormasPago = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getFormasPagoPaginadas(token, page, pageSize, debouncedSearch);
            setFormasPago(data.content);
            setTotalPages(data.totalPages);
            setTotalElements(data.totalElements);
            setError(null);
        } catch (error) {
            console.error('Error al cargar formas de pago:', error);
            setError('No se pudieron cargar las formas de pago.');
            toast.error('Error al cargar las formas de pago');
        } finally {
            setLoading(false);
        }
    }, [token, page, pageSize, debouncedSearch]);

    useEffect(() => {
        cargarFormasPago();
    }, [cargarFormasPago]);

    useEffect(() => {
        setPage(0);
    }, [debouncedSearch, pageSize]);

    const handleEliminar = async (id) => {
        if (window.confirm('¿Estás seguro de que deseas desactivar/eliminar esta forma de pago?')) {
            try {
                await deleteFormaPago(token, id);
                toast.success('Forma de pago eliminada exitosamente');
                cargarFormasPago();
            } catch (error) {
                console.error('Error al eliminar:', error);
                toast.error('No se pudo eliminar la forma de pago. Puede estar en uso.');
            }
        }
    };

    const startItem = totalElements === 0 ? 0 : page * pageSize + 1;
    const endItem = Math.min((page + 1) * pageSize, totalElements);

    return (
        <div className="max-w-6xl mx-auto bg-white shadow-sm border border-gray-200 mb-6 font-sans">
            <Toaster position="top-right" />

            {/* Header */}
            <div className="bg-slate-700 p-3 flex justify-between items-center text-white">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/parametrizacion')}
                        className="p-1 hover:bg-slate-600 rounded transition-colors text-slate-300 hover:text-white"
                        title="Volver a Parametrización"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-blue-400" />
                        <h1 className="text-lg font-semibold tracking-tight">Mantenimiento: Medios y Formas de Pago</h1>
                    </div>
                </div>

                <Link
                    to="/parametrizacion/formas-pago/nuevo"
                    className="flex items-center gap-1 transition-all rounded-sm text-[0.85rem] font-medium px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                >
                    <PlusCircle className="w-4 h-4" />
                    NUEVO MEDIO
                </Link>
            </div>

            {/* Controls */}
            <div className="p-3 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4 bg-gray-50">
                <div className="flex items-center gap-2">
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">Registros:</label>
                    <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        className="border border-gray-300 p-1 bg-white focus:ring-1 focus:ring-blue-500 outline-none text-[0.875rem] rounded-sm disabled:opacity-50"
                        disabled={loading}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                    </select>
                </div>

                <div className="relative">
                    <input
                        type="text"
                        placeholder="Buscar medio de pago..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="border border-gray-300 pl-8 pr-3 py-1.5 w-64 focus:ring-1 focus:ring-blue-500 outline-none text-[0.875rem] rounded-sm bg-white"
                    />
                    <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
                </div>
            </div>

            {/* Content Table Area */}
            <div className="overflow-x-auto relative min-h-[400px]">
                {error && (
                    <div className="m-3 p-3 bg-red-50 border border-red-200 text-red-700 flex items-center rounded-sm text-sm">
                        <AlertCircle className="w-4 h-4 mr-2" />
                        {error}
                    </div>
                )}

                <div className="min-w-full">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-[#f8fafc] border-b-2 border-slate-200 text-gray-700 text-xs uppercase tracking-wider font-bold">
                                <th className="px-4 py-3 border-r border-gray-200 w-16 text-center">ID</th>
                                <th className="px-4 py-3 border-r border-gray-200">Nombre del Medio</th>
                                <th className="px-4 py-3 border-r border-gray-200">Descripción</th>
                                <th className="px-4 py-3 border-r border-gray-200 w-32 text-center">Estado</th>
                                <th className="px-4 py-3 text-center w-40">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm text-gray-600">
                            {loading && formasPago.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-4 py-12 text-center uppercase tracking-widest text-blue-600 font-bold">
                                        Cargando catálogos de tesorería...
                                    </td>
                                </tr>
                            ) : formasPago.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-4 py-12 text-center text-gray-500 italic">
                                        {searchTerm
                                            ? `No se encontraron coincidencias para "${searchTerm}"`
                                            : 'No hay formas de pago configuradas.'}
                                    </td>
                                </tr>
                            ) : (
                                formasPago.map((forma) => (
                                    <tr key={forma.id} className="border-b border-gray-200 hover:bg-[#f1f5f9] transition-colors">
                                        <td className="px-4 py-3 border-r border-gray-200 text-center font-mono text-xs text-gray-400">
                                            {forma.id}
                                        </td>
                                        <td className="px-4 py-3 border-r border-gray-200 font-bold text-slate-800">
                                            {forma.nombre}
                                        </td>
                                        <td className="px-4 py-3 border-r border-gray-200 text-gray-500 italic text-xs">
                                            {forma.descripcion || '- Sin descripción -'}
                                        </td>
                                        <td className="px-4 py-3 border-r border-gray-200 text-center">
                                            {forma.activo ? (
                                                <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[0.65rem] font-black border border-emerald-100 rounded-sm uppercase">Habilitado</span>
                                            ) : (
                                                <span className="px-2 py-0.5 bg-gray-50 text-gray-400 text-[0.65rem] font-black border border-gray-100 rounded-sm uppercase">Deshabilitado</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <div className="flex justify-center items-center gap-2">
                                                <Link
                                                    to={`/parametrizacion/formas-pago/${forma.id}/editar`}
                                                    className="px-2 py-1 text-blue-600 hover:bg-blue-50 font-bold text-[0.65rem] tracking-tighter uppercase transition-colors rounded-sm"
                                                >
                                                    MODIFICAR
                                                </Link>
                                                <span className="text-gray-200">|</span>
                                                <button
                                                    onClick={() => handleEliminar(forma.id)}
                                                    className="px-2 py-1 text-rose-600 hover:bg-rose-50 font-bold text-[0.65rem] tracking-tighter uppercase transition-colors rounded-sm"
                                                >
                                                    ELIMINAR
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Paginator Footer */}
            <div className="p-3 bg-gray-50 flex justify-between items-center border-t border-gray-200 text-sm">
                <div className="text-gray-400 text-xs font-bold uppercase tracking-widest">
                    Página <span className="text-slate-700">{page + 1}</span> de <span className="text-slate-700">{totalPages || 1}</span>
                </div>

                <div className="flex items-center gap-1">
                    <button
                        onClick={() => setPage(page - 1)}
                        disabled={page === 0 || loading}
                        className="p-1 px-3 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50 text-gray-600 rounded-sm font-bold text-xs"
                    >
                        ANT.
                    </button>
                    <button
                        onClick={() => setPage(page + 1)}
                        disabled={page >= totalPages - 1 || loading}
                        className="p-1 px-3 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50 text-gray-600 rounded-sm font-bold text-xs"
                    >
                        SIG.
                    </button>
                </div>
            </div>
        </div>
    );
}
